import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useTimeoutFn } from "@vueuse/core";
import { useIsAnyModalOpen } from "@/composables/buses/use-modal-bus";
import { useUserStore } from "@/stores/user.store";
import { RouteNames } from "@/router/app.router";

type EmailConsentContext =
  | "show"
  | "thread"
  | "contest"
  | "channel"
  | "generic";

const PROMPT_DELAY_MS = 3000;

/**
 * Controls the display logic for the email consent prompt modal.
 *
 * The modal appears after a 3-second delay when:
 * - The user is authenticated and has not yet decided on email consent
 * - The snooze period (if any) has expired
 * - No other modal is currently open
 * - The prompt has not already been shown for the current user session
 *
 * Note: hasBeenShownThisSession resets on logout (when user becomes null)
 * so that the prompt can re-appear after a subsequent login.
 */
export function useEmailConsentPrompt() {
  const userStore = useUserStore();
  const route = useRoute();
  const isAnyModalOpen = useIsAnyModalOpen();

  const isOpen = ref(false);
  const hasBeenShownThisSession = ref(false);

  // Reset the "shown" flag when the user logs out (user becomes null).
  // This ensures the prompt can re-appear after a logout → login cycle,
  // whether it's the same user or a different one.
  watch(
    () => userStore.user,
    (newUser, oldUser) => {
      if (!newUser && oldUser) {
        hasBeenShownThisSession.value = false;
        isOpen.value = false;
      }
    },
  );

  const needsPrompt = computed(() => {
    const user = userStore.user;
    if (!user) return false;

    if (user.email_consent_decided_at != null) return false;

    // Check if snooze period has expired
    if (user.email_consent_snoozed_until) {
      const snoozedUntil = new Date(user.email_consent_snoozed_until);
      if (snoozedUntil > new Date()) return false;
    }

    return true;
  });

  const context = computed<EmailConsentContext>(() => {
    const name = route.name;

    if (name === RouteNames.Show) return "show";
    if (name === RouteNames.ThreadsItem) return "thread";
    if (name === RouteNames.EditQuiz || name === RouteNames.EditRaffle)
      return "contest";
    if (name === RouteNames.Channel || name === RouteNames.ShowList)
      return "channel";

    return "generic";
  });

  const { start, stop } = useTimeoutFn(
    () => {
      isOpen.value = true;
      hasBeenShownThisSession.value = true;
    },
    PROMPT_DELAY_MS,
    { immediate: false },
  );

  watch(
    [needsPrompt, isAnyModalOpen],
    ([needs, anyOpen]) => {
      if (needs && !anyOpen && !hasBeenShownThisSession.value) {
        start();
      } else {
        stop();
      }
    },
    { immediate: true },
  );

  return {
    isOpen,
    context,
  };
}
